// features/automation/components/RunProgressBar.js

import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Loader2, CheckCircle2, XCircle, ChevronUp, ChevronDown, X, CircleCheck, CircleX, MinusCircle, Ban, ExternalLink } from 'lucide-react';
import { resolveRunCounts } from '../../../shared/utils/permutationStatus';
import '../styles/RunProgressBar.css';

const RunProgressBar = ({ runState, isRunning, isCompleted, hasFailures, workerStatus, isDetailsOpen, onToggleDetails, onClose, onGoToResults }) => {
  const hasProgressData = runState?.testResults?.testSummary?.runProgressPercentage > 0 ||
    runState?.testResults?.permutations?.length > 0;
  const isQueuing = isRunning && workerStatus !== 'streaming' && !hasProgressData;
  const trackRef = useRef(null);
  const bubbleRef = useRef(null);
  const rafRef = useRef(null);

  // RAF loop — repositions the portal bubble every frame so it's always attached
  useEffect(() => {
    const tick = () => {
      if (trackRef.current && bubbleRef.current && runState) {
        const pct = runState.testResults.testSummary.runProgressPercentage;
        const rect = trackRef.current.getBoundingClientRect();
        const left = rect.left + (rect.width * pct) / 100;
        const top = rect.top;
        bubbleRef.current.style.left = `${left}px`;
        bubbleRef.current.style.top = `${top}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [runState]);

  if (!runState?.testResults) return null;

  const { testSummary, permutations } = runState.testResults;
  const { runProgressPercentage } = testSummary;
  // Counted by status — a SKIPPED or DISABLED permutation reports hasPassed: true
  // and must not land in the passed tally.
  const { total, executed, passed, failed, skipped, disabled } =
    resolveRunCounts(testSummary, permutations);

  // "2 passed, 0 failed" is misleading when perms were skipped or disabled — name them.
  const doneParts = [`${passed} passed`, `${failed} failed`];
  if (skipped > 0) doneParts.push(`${skipped} skipped`);
  if (disabled > 0) doneParts.push(`${disabled} disabled`);
  const doneLabel = `Done — ${doneParts.join(', ')}${total > executed ? ` (of ${total})` : ''}`;

  return (
    <div className="mock-run-progress__inner">
      {/* Status — left */}
      <div className="mock-run-progress__status">
        {isRunning ? (
          <Loader2 size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--spinning" />
        ) : hasFailures ? (
          <XCircle size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--failed" />
        ) : (
          <CheckCircle2 size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--completed" />
        )}
        <span>
          {isRunning
            ? isQueuing
              ? 'Queuing…'
              : total > 1
                ? `Running · Perm ${executed + (executed < total ? 1 : 0)} of ${total}`
                : 'Running…'
            : doneLabel
          }
        </span>
      </div>

      {/* Progress bar — center */}
      <div className="mock-run-progress__bar-wrap">
        <div className="mock-run-progress__track" ref={trackRef}>
          <div
            className="mock-run-progress__fill"
            style={{ width: `${runProgressPercentage}%` }}
          />
        </div>
      </div>

      {/* Floating percentage bubble — rendered as portal, positioned by RAF loop */}
      {ReactDOM.createPortal(
        <span
          ref={bubbleRef}
          className={`mock-run-progress__bubble${isCompleted ? (hasFailures ? ' mock-run-progress__bubble--failed' : ' mock-run-progress__bubble--completed') : ''}`}
        >
          {runProgressPercentage}%
        </span>,
        document.body
      )}

      {/* Status counts — skipped / disabled only appear when there are any, so the
          common all-executed case stays a two-badge row */}
      <div className="mock-run-progress__counts">
        <span className="mock-run-progress__badge mock-run-progress__badge--passed" title="Passed">
          <CircleCheck size={10} />
          {passed}
        </span>
        <span className="mock-run-progress__badge mock-run-progress__badge--failed" title="Failed">
          <CircleX size={10} />
          {failed}
        </span>
        {skipped > 0 && (
          <span className="mock-run-progress__badge mock-run-progress__badge--skipped" title="Skipped — every step was disabled, so nothing ran">
            <MinusCircle size={10} />
            {skipped}
          </span>
        )}
        {disabled > 0 && (
          <span className="mock-run-progress__badge mock-run-progress__badge--disabled" title="Disabled — turned off in the data source">
            <Ban size={10} />
            {disabled}
          </span>
        )}
      </div>

      {/* Actions — right */}
      <div className="mock-run-progress__actions">
        <button
          type="button"
          className={`mock-run-progress__view-btn${isDetailsOpen ? ' mock-run-progress__view-btn--active' : ''}`}
          onClick={onToggleDetails}
        >
          {isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          <span>{isDetailsOpen ? 'Hide' : 'Details'}</span>
        </button>
        {isCompleted && (
          <button
            type="button"
            className={`mock-run-progress__results-btn${hasFailures ? ' mock-run-progress__results-btn--failed' : ''}`}
            onClick={onGoToResults}
          >
            <ExternalLink size={11} />
            <span>Results</span>
          </button>
        )}
        {isCompleted && (
          <button
            type="button"
            className="mock-run-progress__close-btn"
            onClick={onClose}
            title="Close"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

RunProgressBar.propTypes = {
  runState: PropTypes.object,
  isRunning: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  hasFailures: PropTypes.bool,
  workerStatus: PropTypes.string,
  isDetailsOpen: PropTypes.bool.isRequired,
  onToggleDetails: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGoToResults: PropTypes.func.isRequired,
};

RunProgressBar.defaultProps = {
  runState: null,
  hasFailures: false,
  workerStatus: 'idle',
};

export default RunProgressBar;
